import { Reducer } from "redux";
import {
    SET_CC_ABSOLUTE,
    SET_CC_REDISPATCHING,
    SET_CC_RELATIVE,
    SET_CC_RETURNED_BRANCHES,
    SET_CC_SENSITIVITY_THRESHOLD,
    SET_CC_VALIDATION,
    SET_CC_VALIDATION_THRESHOLD,
    SET_CC_VREFOPTION,
    SET_IVA_EQUATION,
    SET_IVA_SELECTION,
    SET_LOOP_FLOW_THRESHOLD,
    SET_MANUAL_PSTS,
    SET_MANUAL_TOPO_RAS,
    SET_MNECS_FOR_OPTIMIZATION,
    SET_TOGGLE_LOOP_FLOW,
    SET_RCDC_FIX,
    SET_MAX_RDR_UP,
    SET_MAX_RDR_DOWN,
    SET_ALL_RD_SWITCHABLE,
    SET_CC_IVA_TRESHOLD,
    SET_CC_USE_MIN_RAM_FOR_IVA,
    SET_CC_IVA_MIN_RAM_PERCENTAGE,
    SET_RANGE_FROM_INITIAL,
    SET_DEFAULT_UPWARDS,
    SET_DEFAULT_DOWNWARDS,
    SET_SCALE_BY_LOAD,
    SET_WEIGH_DISTANCE,
} from "../actions/ccValidationActions";

export const vRefOptions = [
    { key: 0, value: 0, text: "RefProg" },
    { key: 1, value: 1, text: "Zero Balance" },
];

export const ivaSelectionOptions = [
    { key: 0, value: 0, text: "Max" },
    { key: 1, value: 1, text: "Average" },
    { key: 2, value: 2, text: "Min" },
];

export const ivaEquationOptions = [
    {
        key: 0,
        text: "0. IVA = max(- RAM_afterRAO_Vi + CC_val_threshold, 0)",
        value: 0,
    },
    {
        key: 1,
        text: "1. IVA = -RAM_after_RAO_stepN - RAM_after_RAO_vertex + CC_val_threshold",
        value: 1,
    },
    {
        key: 2,
        text: "2. IVA = -PTDF*(NP_Vn-NP_Vi) - RAM_afterRAO_Vn + CC_val_threshold",
        value: 2,
    },
];

export interface CCValidationState {
    vRefOption: number;
    absolute: number;
    relative: number;
    redispatching: boolean;
    returnedBranches: boolean;
    sensitivityThreshold: number;
    validationThreshold: number;
    ivaThreshold: number;
    useMinRamForIva: boolean;
    ivaMinRamPercentage: number;
    ivaSelection: number;
    ivaEquation: number;
    mnecsForOptimization: boolean;
    toggleLoopFlow: boolean;
    loopFlowThreshold: number;
    manualPSTs: boolean;
    manualTopoRAs: boolean;
    rcdcFix: boolean;
    maxRdrUp: number;
    maxRdrDown: number;
    allRdSwitchable: boolean;
    rangeFromInitial: boolean;
    defaultUpwards: number;
    defaultDownwards: number;
    scaleByLoad: boolean;
    weightDistance: boolean;
}

export const initialCCValidationState: CCValidationState = {
    vRefOption: 0,
    absolute: 0,
    relative: 1,
    redispatching: true,
    returnedBranches: true,
    sensitivityThreshold: 5,
    validationThreshold: 0,
    ivaThreshold: 0,
    useMinRamForIva: false,
    ivaMinRamPercentage: 0,
    ivaSelection: 0,
    ivaEquation: 0,
    mnecsForOptimization: false,
    toggleLoopFlow: false,
    loopFlowThreshold: 0.1,
    manualPSTs: true,
    manualTopoRAs: true,
    rcdcFix: true,
    maxRdrUp: 0.1,
    maxRdrDown: 0.1,
    allRdSwitchable: true,
    rangeFromInitial: false,
    defaultUpwards: 0,
    defaultDownwards: 0,
    scaleByLoad: false,
    weightDistance: false,
};

export const ccValidationReducer: Reducer<CCValidationState> = (
    state: CCValidationState = initialCCValidationState,
    action: any
): CCValidationState => {
    switch (action.type) {
        case SET_CC_VALIDATION:
            return { ...action.state };
        case SET_CC_VREFOPTION:
            return { ...state, vRefOption: action.value };
        case SET_CC_ABSOLUTE:
            return { ...state, absolute: action.value };
        case SET_CC_RELATIVE:
            return { ...state, relative: action.value };
        case SET_CC_REDISPATCHING:
            return { ...state, redispatching: action.opt };
        case SET_CC_RETURNED_BRANCHES:
            return { ...state, returnedBranches: action.opt };
        case SET_CC_SENSITIVITY_THRESHOLD:
            return { ...state, sensitivityThreshold: action.value };
        case SET_CC_VALIDATION_THRESHOLD:
            return { ...state, validationThreshold: action.value };
        case SET_CC_IVA_TRESHOLD:
            return { ...state, ivaThreshold: action.value };
        case SET_CC_USE_MIN_RAM_FOR_IVA:
            return { ...state, useMinRamForIva: action.value };
        case SET_CC_IVA_MIN_RAM_PERCENTAGE:
            return { ...state, ivaMinRamPercentage: action.value };
        case SET_IVA_SELECTION:
            return {
                ...state,
                ivaSelection: action.selection,
            };
        case SET_IVA_EQUATION:
            return {
                ...state,
                ivaEquation: action.selection,
            };
        case SET_MNECS_FOR_OPTIMIZATION:
            return {
                ...state,
                mnecsForOptimization: action.opt,
            };
        case SET_TOGGLE_LOOP_FLOW:
            return {
                ...state,
                toggleLoopFlow: action.opt,
            };
        case SET_LOOP_FLOW_THRESHOLD:
            return {
                ...state,
                loopFlowThreshold: action.value,
            };
        case SET_MANUAL_PSTS:
            return { ...state, manualPSTs: action.opt };
        case SET_MANUAL_TOPO_RAS:
            return { ...state, manualTopoRAs: action.opt };
        case SET_RANGE_FROM_INITIAL:
            return { ...state, rangeFromInitial: action.opt };
        case SET_DEFAULT_UPWARDS:
            return { ...state, defaultUpwards: action.opt };
        case SET_DEFAULT_DOWNWARDS:
            return { ...state, defaultDownwards: action.opt };
        case SET_RCDC_FIX:
            return { ...state, rcdcFix: action.opt };
        case SET_MAX_RDR_UP:
            return { ...state, maxRdrUp: action.value };
        case SET_MAX_RDR_DOWN:
            return { ...state, maxRdrDown: action.value };
        case SET_ALL_RD_SWITCHABLE:
            return { ...state, allRdSwitchable: action.opt };
        case SET_SCALE_BY_LOAD:
            return {
                ...state,
                scaleByLoad: action.selection,
            };
        case SET_WEIGH_DISTANCE:
            return {
                ...state,
                weightDistance: action.selection,
            };
        default:
            return state;
    }
};
